import type { ComponentChildren } from "preact";
import { useFocusTrap } from "./use-focus-trap";

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  children: ComponentChildren;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Called when backdrop/ESC is used to dismiss. Defaults to onCancel if not provided. */
  onDismiss?: () => void;
};

export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  onDismiss,
}: ConfirmDialogProps) {
  const handleDismiss = onDismiss ?? onCancel;
  const dialogRef = useFocusTrap<HTMLDivElement>({
    open,
    onEscape: handleDismiss,
  });

  if (!open) return null;

  return (
    <div
      class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      {/* Backdrop */}
      <div
        class="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleDismiss}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        class="relative w-full max-w-sm bg-surface rounded-xl shadow-xl animate-scale-in"
      >
        <div class="p-6">
          <h3
            id="confirm-dialog-title"
            class="text-lg font-semibold text-text mb-2"
          >
            {title}
          </h3>
          <div class="text-sm text-text-muted">{children}</div>
        </div>

        <div class="flex gap-3 px-6 pb-6">
          <button
            type="button"
            onClick={onConfirm}
            class="flex-1 px-4 py-2 rounded-lg bg-text text-surface text-sm font-medium hover:opacity-90 transition-opacity"
          >
            {confirmLabel}
          </button>
          <button
            type="button"
            onClick={onCancel}
            class="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:bg-surface-hover transition-colors"
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
